import { CheckIcon, Moon, Sun } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";

const LandingPage = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  // Referencias para el scroll a secciones
  const funcionesRef = useRef<HTMLDivElement>(null);
  const funcionamientoRef = useRef<HTMLDivElement>(null);
  const preciosRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll a las secciones
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="py-4 px-6 bg-background header-shadow sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <div className="flex items-center group">
              <div className="h-16 w-16 mr-3">
                {" "}
                {/* Increased size from h-10 w-10 to h-16 w-16 */}
                <img
                  src="/images/logo.png"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => scrollToSection(funcionesRef)}
              className="text-foreground hover:text-primary"
            >
              Funcionalidades
            </Button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection(funcionamientoRef)}
              className="text-foreground hover:text-primary"
            >
              Cómo funciona
            </Button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection(preciosRef)}
              className="text-foreground hover:text-primary"
            >
              Precios
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/auth">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Iniciar sesión
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-12 md:py-24 container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                Accede al BOE como nunca antes: Filtros, Búsquedas y Alertas
                Personalizadas
              </h1>
              <p className="text-xl text-muted-foreground">
                Encuentra lo que necesitas en segundos.
              </p>
              <Link to="/auth">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                  Prueba Ahora
                </Button>
              </Link>
            </div>
            <div className="rounded-lg border bg-card p-8 shadow-sm">
              <div className="aspect-video flex items-center justify-center bg-muted/30 rounded-md">
                <p className="text-muted-foreground">Mockup de la app aquí</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section ref={funcionesRef} className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4">
                ¿Qué puedes hacer con BOE Fácil?
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: "Búsqueda por palabras",
                  description:
                    "Encuentra rápidamente cualquier documento con nuestra búsqueda avanzada.",
                  icon: "🔍",
                },
                {
                  title: "Filtros personalizados",
                  description:
                    "Filtra por fecha, categoría o tipo de documento en segundos.",
                  icon: "🔢",
                },
                {
                  title: "Alertas diarias",
                  description:
                    "Recibe notificaciones automáticas cuando se publique algo que te interesa.",
                  icon: "🔔",
                },
                {
                  title: "Actualización diaria",
                  description:
                    "Nuestra base de datos se actualiza cada día con el BOE más reciente.",
                  icon: "🔄",
                },
              ].map((feature, i) => (
                <Card
                  key={i}
                  className="border-0 shadow-md hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="text-4xl mb-2">{feature.icon}</div>
                      <h3 className="font-bold text-lg">{feature.title}</h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Detailed Features Section */}
        <section className="py-16 container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Funcionalidades detalladas
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Descubre todas las herramientas que ponemos a tu disposición para
              simplificar tu relación con el BOE
            </p>
          </div>

          {/* Feature 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center">
            <div className="order-2 md:order-1">
              <h3 className="text-2xl font-bold mb-4">
                Sistema de búsqueda avanzada
              </h3>
              <p className="text-muted-foreground mb-6">
                Nuestro motor de búsqueda te permite encontrar exactamente lo
                que necesitas:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Búsqueda por palabras clave, con resaltado de coincidencias
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Búsqueda avanzada con operadores booleanos (AND, OR, NOT)
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Filtros por fecha, departamento, sección y tipo de
                    disposición
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>Resultados instantáneos con previsualización</span>
                </li>
              </ul>
            </div>
            <div className="order-1 md:order-2 rounded-lg border bg-card p-6 shadow-md">
              <div className="aspect-video flex items-center justify-center bg-muted/30 rounded-md">
                <p className="text-muted-foreground">
                  Imagen de búsqueda avanzada
                </p>
              </div>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center">
            <div className="rounded-lg border bg-card p-6 shadow-md">
              <div className="aspect-video flex items-center justify-center bg-muted/30 rounded-md">
                <p className="text-muted-foreground">
                  Imagen de sistema de alertas
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">
                Sistema de alertas personalizado
              </h3>
              <p className="text-muted-foreground mb-6">
                Configura alertas para estar al día con las publicaciones que te
                interesan:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>Alertas por palabras clave o términos específicos</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Filtrado por secciones o departamentos específicos
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Notificaciones diarias cuando se publica contenido relevante
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Gestión fácil de múltiples alertas desde tu dashboard
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h3 className="text-2xl font-bold mb-4">
                Acceso a legislación consolidada
              </h3>
              <p className="text-muted-foreground mb-6">
                Consulta la versión más actualizada de las normas vigentes:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Textos actualizados con todas las modificaciones
                    incorporadas
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Historial de versiones y cambios en la legislación
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Referencias cruzadas entre normativas relacionadas
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-3 flex-shrink-0">
                    ✓
                  </span>
                  <span>Exportación a PDF para consulta offline</span>
                </li>
              </ul>
            </div>
            <div className="order-1 md:order-2 rounded-lg border bg-card p-6 shadow-md">
              <div className="aspect-video flex items-center justify-center bg-muted/30 rounded-md">
                <p className="text-muted-foreground">
                  Imagen de legislación consolidada
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup Section */}
        <section
          ref={funcionamientoRef}
          className="py-16 container mx-auto px-4 md:px-6"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold">Mira cómo funciona</h2>
            <p className="text-xl text-muted-foreground mt-4 max-w-3xl mx-auto">
              Una interfaz intuitiva diseñada para facilitar la consulta del
              BOE, tanto para profesionales jurídicos como para ciudadanos
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border">
              <p className="text-muted-foreground">Mockup de la app aquí</p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="font-bold mb-2">Busca</h3>
                <p className="text-muted-foreground">
                  Utiliza nuestro potente buscador para encontrar las
                  disposiciones que te interesan
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="font-bold mb-2">Filtra</h3>
                <p className="text-muted-foreground">
                  Refina los resultados con nuestros filtros avanzados para
                  mayor precisión
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="font-bold mb-2">Configura</h3>
                <p className="text-muted-foreground">
                  Crea alertas personalizadas para recibir notificaciones
                  automáticas
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section ref={preciosRef} className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Planes a tu medida</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Elige el plan que mejor se adapte a tus necesidades
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Plan Gratuito */}
              <Card className="shadow-md border-0">
                <CardHeader>
                  <CardTitle className="text-xl">Gratuito</CardTitle>
                  <CardDescription>Funcionalidades básicas</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">€0</span>
                    <span className="text-muted-foreground">/mes</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Búsqueda básica</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Filtros estándar</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Hasta 5 alertas</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Acceso a legislación básica</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Link to="/auth" className="w-full">
                    <Button variant="outline" className="w-full">
                      Registrarse
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              {/* Plan Premium Basic */}
              <Card className="shadow-md border-t-4 border-t-blue-500 border-r-0 border-l-0 border-b-0 relative">
                <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs rounded-bl-lg">
                  POPULAR
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">Premium Basic</CardTitle>
                  <CardDescription>Para uso profesional</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">€9.99</span>
                    <span className="text-muted-foreground">/mes</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Búsqueda avanzada</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Filtros personalizados</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Hasta 20 alertas</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Acceso a legislación completa</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Exportación a PDF</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Link to="/auth" className="w-full">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      Suscribirse
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              {/* Plan Premium Pro */}
              <Card className="shadow-md border-0">
                <CardHeader>
                  <CardTitle className="text-xl">Premium Pro</CardTitle>
                  <CardDescription>
                    Para equipos e instituciones
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">€19.99</span>
                    <span className="text-muted-foreground">/mes</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Todo lo de Premium Basic</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Alertas ilimitadas</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Histórico completo</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>API para integración</span>
                    </li>
                    <li className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span>Soporte prioritario</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Link to="/auth" className="w-full">
                    <Button variant="outline" className="w-full">
                      Suscribirse
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center md:text-left">
            <Link
              to="#"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Acerca de
            </Link>
            <Link
              to="#"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Contacto
            </Link>
            <Link
              to="#"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Términos y Condiciones
            </Link>
            <Link
              to="#"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Política de Privacidad
            </Link>
            <div className="text-sm text-muted-foreground">
              © 2025 BOE Fácil
            </div>
          </div>
        </div>
      </footer>

      {/* Theme Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed bottom-4 left-4 p-3 rounded-full bg-background border shadow-lg hover:shadow-xl transition-all duration-200 z-50"
        aria-label="Cambiar tema"
      >
        {darkMode ? (
          <Sun className="h-5 w-5 text-yellow-500" />
        ) : (
          <Moon className="h-5 w-5 text-slate-700" />
        )}
      </button>
    </div>
  );
};

export default LandingPage;
